import React, { useState } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { showToast as toast } from '../toastManager';
import axios from 'axios';
import Cookies from 'js-cookie';
import { applyLoginPayloadToCookies } from '../authCookies';

/**
 * First-time Shopify store onboarding (parity with Admin Dashboard → Accounts → Create New Account).
 * User reaches this route when authenticated with needs_store_setup cookie (no store linked yet).
 */
function generateShopId(accountName) {
  const slug = String(accountName || '').trim().toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_')
    .replace(/^-|-$/g, '') || 'account';
  //const suffix = Date.now().toString(36).slice(-4);
  return `${slug}`; //-${suffix}`;
}

function StoreSetup({ app_url }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    shop_name: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const clearAuthCookies = () => {
    const keys = [
      'shop',
      'shop_name',
      'stores',
      'allowed_pages_json',
      'show_under_dev_pages',
      'user_email',
      'user_role',
      'user_permissions',
      'dev_page_permissions_by_store',
      'needs_store_setup',
    ];
    keys.forEach((k) => Cookies.remove(k, {
      secure: process.env.REACT_APP_WENV !== "local",
      domain: process.env.REACT_APP_WENV !== "local" ? ".cognitiveux.ai" : "",
      path: "/",
    }));
  };

  const handleSignOut = async () => {
    try {
      await axios.post(`${app_url}/api/signout`, {}, { withCredentials: true });
    } catch (_) {
      /* still clear cookies */
    }
    clearAuthCookies();
    navigate('/login', { replace: true });
    window.location.reload();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.shop_name?.trim() || form.shop_name.trim().length > 50) {
      toast.error('Enter an account name (max 50 characters)');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(
        `${app_url}/setup-store`,
        {
          shop: generateShopId(form.shop_name),
          shop_name: form.shop_name.trim(),
          account_type: 'live',
        },
        { withCredentials: true }
      );
      if (r.data?.success) {
        toast.success(r.data?.message || 'Store created successfully');
        applyLoginPayloadToCookies(r.data, null); // set cookies without navigating
        navigate('/account-setup', { replace: true });
        window.location.reload();
      } else {
        toast.error(r.data?.message || 'Failed to create store');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create store');
    } finally {
      setLoading(false);
    }
  };

  if (!Cookies.get('user_email') || Cookies.get('needs_store_setup') !== 'true') {
    return (
      <>
        <Toaster />
        <div
          style={{
            minHeight: '100vh',
            backgroundColor: 'var(--cux-ink, #132848)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div style={{ color: 'white', textAlign: 'center' }}>
            <p>Session expired or store already configured.</p>
            <button
              type="button"
              className="btn btn-link"
              style={{ color: 'var(--cux-accent-muted, #93c5fd)' }}
              onClick={() => navigate('/login', { replace: true })}
            >
              Log in
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster />
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--cux-ink, #132848)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '480px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#1a1a1a',
              marginBottom: '8px',
              marginTop: 0,
            }}
          >
            Set up your account
          </h1>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
            Enter your account name to finish onboarding.
          </p>

          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3" controlId="setupShopName">
              <Form.Label style={{ fontSize: '14px', fontWeight: '500' }}>Account Name</Form.Label>
              <Form.Control
                type="text"
                name="shop_name"
                value={form.shop_name}
                onChange={handleChange}
                placeholder="Your account display name (max 50 characters)"
                maxLength={50}
                required
                style={{ padding: '8px', borderRadius: '8px' }}
              />
            </Form.Group>

            <Button
              type="submit"
              disabled={loading}
              className="w-100 mb-3"
              style={{
                backgroundColor: 'var(--cux-ink, #132848)',
                borderColor: 'var(--cux-ink, #132848)',
                padding: '10px',
                borderRadius: '8px',
                fontWeight: '600',
              }}
            >
              {loading ? <Spinner size="sm" /> : 'Create account'}
            </Button>
          </Form>

          <div style={{ textAlign: 'center', fontSize: '14px', color: '#6b7280' }}>
            <button type="button" className="btn btn-link p-0" style={{ color: 'var(--cux-ink, #132848)' }} onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default StoreSetup;
